import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FiUser, FiCreditCard, FiFileText, FiSettings } from "react-icons/fi";
import ProfileSection from "./ProfileSection";
import BillingSection from "./BillingSection";
import PaymentMethodsSection from "./PaymentMethodsSection";
import SubscriptionSection from "./SubscriptionSection";
import StripeProvider from "../../components/stripe/StripeProvider";
import { useUserContext } from "../../context/UserContext";

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();
  
  // Get user context for role-based access
  const { userRole, isAdmin } = useUserContext();
  
  // Determine if user is a branch user (non-admin)
  const isBranchUser = !isAdmin;
  
  // For branch users, only show Profile tab
  const availableTabs = isBranchUser ? ['profile'] : ['profile', 'billing', 'payment-methods', 'subscription'];

  // Handle URL parameters for direct tab access
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam && !isBranchUser) {
      switch (tabParam) {
        case 'subscription':
          setActiveTab(3); // Subscription tab index
          break;
        case 'billing':
          setActiveTab(1); // Billing tab index
          break;
        case 'payment-methods':
          setActiveTab(2); // Payment Methods tab index
          break;
        default:
          setActiveTab(0); // Profile tab index
      }
    }
  }, [isBranchUser]);

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  // Ensure branch users can only access Profile tab
  useEffect(() => {
    if (isBranchUser && activeTab !== 0) {
      setActiveTab(0);
      toast({
        title: "Access Restricted",
        description: "You can only access the Profile section.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  }, [isBranchUser, activeTab, toast]);

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Settings
            </Heading>
            <Text color="gray.600" fontSize="md">
              {isBranchUser 
                ? "Manage your profile information and account settings"
                : "Manage your account settings and billing preferences"
              }
            </Text>
          </VStack>
        </HStack>

        {/* Access Level Notice for Branch Users */}
        {isBranchUser && (
          <Alert status="info" mb={4}>
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Limited Access</Text>
              <Text fontSize="sm">
                As a branch user, you can only manage your profile information. 
                Billing and subscription management is restricted to administrators.
              </Text>
            </Box>
          </Alert>
        )}

        {/* Settings Tabs */}
        <Card>
          <CardBody p={0}>
            <Tabs index={activeTab} onChange={handleTabChange} variant="enclosed">
              <TabList bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                {/* Profile Tab - Available to all users */}
                <Tab
                  _selected={{ bg: "white", borderBottomColor: "white" }}
                  _hover={{ bg: "gray.100" }}
                  py={4}
                  px={6}
                >
                  <HStack spacing={2}>
                    <FiUser size={18} />
                    <Text fontWeight="500">Profile</Text>
                  </HStack>
                </Tab>
                
                {/* Admin-only tabs */}
                {!isBranchUser && (
                  <>
                    <Tab
                      _selected={{ bg: "white", borderBottomColor: "white" }}
                      _hover={{ bg: "gray.100" }}
                      py={4}
                      px={6}
                    >
                      <HStack spacing={2}>
                        <FiCreditCard size={18} />
                        <Text fontWeight="500">Billing</Text>
                      </HStack>
                    </Tab>
                    <Tab
                      _selected={{ bg: "white", borderBottomColor: "white" }}
                      _hover={{ bg: "gray.100" }}
                      py={4}
                      px={6}
                    >
                      <HStack spacing={2}>
                        <FiSettings size={18} />
                        <Text fontWeight="500">Payment Methods</Text>
                      </HStack>
                    </Tab>
                    <Tab
                      _selected={{ bg: "white", borderBottomColor: "white" }}
                      _hover={{ bg: "gray.100" }}
                      py={4}
                      px={6}
                    >
                      <HStack spacing={2}>
                        <FiFileText size={18} />
                        <Text fontWeight="500">Subscription</Text>
                      </HStack>
                    </Tab>
                  </>
                )}
              </TabList>

              <TabPanels>
                {/* Profile Panel - Always available */}
                <TabPanel p={6}>
                  <ProfileSection />
                </TabPanel>
                
                {/* Billing Panel - Admin only */}
                <TabPanel p={6}>
                  {!isBranchUser ? <BillingSection /> : <ProfileSection />}
                </TabPanel>
                
                {/* Payment Methods Panel - Admin only */}
                <TabPanel p={6}>
                  {!isBranchUser ? (
                    <StripeProvider>
                      <PaymentMethodsSection />
                    </StripeProvider>
                  ) : (
                    <ProfileSection />
                  )}
                </TabPanel>
                
                {/* Subscription Panel - Admin only */}
                <TabPanel p={6}>
                  {!isBranchUser ? <SubscriptionSection /> : <ProfileSection />}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default Settings;